/**
 * REST contract: zod schemas + inferred types for every request/response.
 *
 * Server validates inbound with these; the client parses outbound responses
 * with them. Types are z.infer<> — never hand-written twice. FROZEN after WS-0.
 */
import { z } from 'zod';
import { LIMITS, PLAYBACK_RATES } from './constants.js';
import { ErrorCode } from './errors.js';
import { MediaDetailSchema, MediaSummarySchema, MediaStatusSchema, TranscodeJobSchema, } from './media.js';
import { PlaybackStateSchema, RoleSchema, RoomSummarySchema } from './rooms.js';
// ── Health / Ready ────────────────────────────────────────────────────────
export const GetHealthResponseSchema = z.object({
    status: z.literal('ok'),
    version: z.string(),
    uptimeSec: z.number().nonnegative(),
});
export const GetReadyResponseSchema = z.object({
    ready: z.boolean(),
    db: z.boolean(),
    ffmpeg: z.boolean(),
});
// ── Auth ──────────────────────────────────────────────────────────────────
export const PostSessionRequestSchema = z
    .object({
    displayName: z
        .string()
        .min(1, 'Display name is required')
        .max(LIMITS.DISPLAY_NAME_MAX, `Display name must be <= ${LIMITS.DISPLAY_NAME_MAX} chars`),
})
    .strict();
export const SessionUserSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
});
export const PostSessionResponseSchema = z.object({
    token: z.string(),
    user: SessionUserSchema,
    expiresAt: z.number().int(),
});
// ── Session revocation ───────────────────────────────────────────────────
// POST /api/auth/revoke — revokes the caller's current session JWT so it
// can no longer be used for REST or socket auth. Returns 200 on success.
export const PostRevokeSessionResponseSchema = z.object({
    revoked: z.literal(true),
});
// ── Media ─────────────────────────────────────────────────────────────────
export const GetMediaListQuerySchema = z
    .object({
    q: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    status: MediaStatusSchema.optional(),
})
    .strict();
export const GetMediaListResponseSchema = z.object({
    items: z.array(MediaSummarySchema),
    total: z.number().int().nonnegative(),
});
export const GetMediaDetailResponseSchema = MediaDetailSchema;
export const DeleteMediaResponseSchema = z.object({ deleted: z.literal(true) });
// ── Rooms ─────────────────────────────────────────────────────────────────
export const PostRoomsRequestSchema = z
    .object({
    mediaId: z.string().min(1),
    name: z
        .string()
        .min(1)
        .max(LIMITS.ROOM_NAME_MAX, `Room name must be <= ${LIMITS.ROOM_NAME_MAX} chars`),
})
    .strict();
export const PostRoomsResponseSchema = z.object({
    room: RoomSummarySchema,
    joinCode: z.string(),
    invitationCode: z.string(),
    hostKey: z.string(),
    role: z.literal('host'),
});
// ── Join by invitation code ───────────────────────────────────────────────
// POST /api/rooms/join-by-code — resolves an admin-shared invitation code to
// the room summary so a normal user can navigate to /room/:id. Does NOT
// require an admin token; any authenticated user may call it.
export const PostJoinByCodeRequestSchema = z
    .object({ invitationCode: z.string().min(1).max(64) })
    .strict();
export const PostJoinByCodeResponseSchema = z.object({
    room: RoomSummarySchema,
});
export const GetRoomResponseSchema = RoomSummarySchema;
export const PostJoinRoomRequestSchema = z.object({}).strict();
export const PostJoinRoomResponseSchema = z.object({
    room: RoomSummarySchema,
    role: RoleSchema,
    state: PlaybackStateSchema,
    media: MediaDetailSchema,
});
export const DeleteRoomResponseSchema = z.object({ deleted: z.literal(true) });
// ── Active rooms (host home screen) ──────────────────────────────────────
// WHY: this endpoint is admin-token-guarded, so it may expose each room's host
// key (and invitation code) so an admin can take over as host. Never add these
// to RoomSummarySchema — that one is returned to ordinary viewers.
export const AdminRoomSummarySchema = RoomSummarySchema.extend({
    hostKey: z.string().nullable(),
    invitationCode: z.string().nullable(),
});
export const GetActiveRoomsResponseSchema = z.object({
    rooms: z.array(AdminRoomSummarySchema),
});
// ── Kick / blacklist ──────────────────────────────────────────────────────
export const KickMemberResponseSchema = z.object({ kicked: z.literal(true) });
export const BlacklistResponseSchema = z.object({ blacklisted: z.literal(true) });
// ── Media token refresh (WS-4) ───────────────────────────────────────────
// 204 No Content + Set-Cookie; no body schema.
// ── Admin ────────────────────────────────────────────────────────────────
export const PostRescanRequestSchema = z
    .object({
    mediaId: z.string().min(1).optional(),
})
    .strict();
export const PostRescanResponseSchema = z.object({ queued: z.number().int().nonnegative() });
// ── Admin: source library browser ────────────────────────────────────────
/** One video file found under MEDIA_DIR, with its syncreel registration state. */
export const LibraryFileSchema = z.object({
    /** Path relative to MEDIA_DIR (POSIX separators). */
    relPath: z.string(),
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    modifiedAt: z.number().nonnegative(),
    /** Sidecar subtitle files (.srt/.vtt/.ass/.ssa) found next to the video. */
    subtitleCount: z.number().int().nonnegative(),
    /** Set once the file has been added to the syncreel library. */
    mediaId: z.string().nullable(),
    status: MediaStatusSchema.nullable(),
});
export const GetLibraryResponseSchema = z.object({ files: z.array(LibraryFileSchema) });
export const PostLibraryAddRequestSchema = z
    .object({ relPath: z.string().min(1).max(1024) })
    .strict();
export const PostLibraryAddResponseSchema = z.object({
    mediaId: z.string(),
    status: MediaStatusSchema,
    /** True when the file was already in the library (a failed one is re-queued). */
    alreadyAdded: z.boolean(),
});
// ── Admin: uploads + URL downloads ───────────────────────────────────────
export const PostUploadResponseSchema = z.object({
    mediaId: z.string(),
    status: MediaStatusSchema,
});
export const DownloadStatusSchema = z.enum(['queued', 'downloading', 'done', 'failed', 'cancelled']);
export const DownloadSchema = z.object({
    id: z.string(),
    /** Origin + path only; the query string (often a signed token) is never returned. */
    url: z.string(),
    status: DownloadStatusSchema,
    bytesDone: z.number().int().nonnegative(),
    bytesTotal: z.number().int().nonnegative().nullable(),
    error: z.string().nullable(),
    mediaId: z.string().nullable(),
    createdAt: z.number(),
});
export const PostDownloadRequestSchema = z.object({ url: z.string().min(1).max(2048) }).strict();
export const GetDownloadsResponseSchema = z.object({ downloads: z.array(DownloadSchema) });
export const DeleteDownloadResponseSchema = z.object({ deleted: z.literal(true) });
export const GetJobsResponseSchema = z.object({ jobs: z.array(TranscodeJobSchema) });
// ── Socket error event (shared shape) ────────────────────────────────────
export const SocketErrorSchema = z.object({
    code: z.nativeEnum(ErrorCode),
    message: z.string(),
});
// Re-export the rate enum for convenience in route validation.
export { PLAYBACK_RATES };
//# sourceMappingURL=rest.js.map